import _ from "lodash";
import {FormEvent, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Pref, UserPref, useSetTenantPrefMutation} from "../../../../generated/graphql";
import {FeatureFlagContext} from "../../../../providers/FeatureFlagProvider";
import {extractSimplePref, PreferenceContext, PrefValue} from "../../../../providers/PreferenceProvider";
import {TenantPreferences} from "../../../../constants/constants";
import {ManifestStatusType} from "../../ColorSettings/ColorizedIndicators/types/manifestAttributes";
import {DriverRequirementEnforcementLevel} from "../../enums/DriverRequirementEnforcementLevel";
import {OrderEntryType} from "../../enums/OrderEntry";
import "../common/CompanySettings.css";
import {ContentProps} from "../common/components/Content";
import Section from "../common/components/Section";
import {
  ASSIGNMENT_SUBMITTED_NOTIFICATION_DEFAULT,
  ContentNumericProp,
  ContentOptionProps,
  DEFAULT_SUGGESTED_ASSIGNMENTS_PER_DAY,
  MIN_SUGGESTED_ASSIGNMENTS_PER_DAY
} from "../common/constants";
import {renderNumericTextBox, renderRadioGroupProps} from "../common/renderer";
import {Body, CancelButton, Container, Footer, Header, HeaderText, SaveButton} from "../common/styles";
import log from "loglevel";
import {useAuthState} from "../../../../providers/AuthProvider";

const General = () => {
  const {tenantPreferences, tenantPrefsQueryRefetch} = useContext(PreferenceContext);
  const {isSuperUser} = useAuthState();
  const [setTenantPref, {loading: isSetTenantPrefLoading, data: setTenantPrefData}] = useSetTenantPrefMutation();
  const {
    driverRequirements: driverRequirementsEnabled,
    idexxLogicCompleteStops: idexxFeatureEnabled,
    askManifestCheckoutInfo: askManifestCheckoutInfoEnabled,
    skills: skillsEnabled,
    closedNeverReassign: closedNeverReassignEnabled,
    suggestedAssignment: suggestedAssignmentEnabled
  } = useContext(FeatureFlagContext);

  const [orderEntryType, setOrderEntryType] = useState<OrderEntryType>(OrderEntryType.Current);
  const [initOrderEntryType, setInitOrderEntryType] = useState<OrderEntryType | null>(null);

  const [timezoneOfJobStop, setTimezoneOfJobStop] = useState<boolean>(false);
  const [initTimezoneOfJobStop, setInitTimezoneOfJobStop] = useState<boolean>(false);

  const [requireArrAndDepTimes, setRequireArrAndDepTimes] = useState<boolean>(false);
  const [initRequireArrAndDepTimes, setInitRequireArrAndDepTimes] = useState<boolean>(false);

  const [canUnassignPartialJob, setCanUnassignPartialJob] = useState<boolean>(false);
  const [initCanUnassignPartialJob, setInitCanUnassignPartialJob] = useState<boolean>(false);

  // DP-3231: default Yes, so both start at the default or the page loads dirty (DP-3166)
  const [assignmentSubmittedNotification, setAssignmentSubmittedNotification] = useState<boolean>(
    ASSIGNMENT_SUBMITTED_NOTIFICATION_DEFAULT
  );
  const [initAssignmentSubmittedNotification, setInitAssignmentSubmittedNotification] = useState<boolean>(
    ASSIGNMENT_SUBMITTED_NOTIFICATION_DEFAULT
  );

  const [defaultManifestStatus, setDefaultManifestStatus] = useState<ManifestStatusType | null>(null);
  const [initDefaultManifestStatus, setInitDefaultManifestStatus] = useState<ManifestStatusType | null>(null);

  const [idexxCompleteStopLogic, setIdexxCompleteStopLogic] = useState<boolean>(false);
  const [initIdexxCompleteStopLogic, setInitIdexxCompleteStopLogic] = useState<boolean>(false);

  const [askCheckoutTimeAndMileage, setAskCheckoutTimeAndMileage] = useState<boolean>(false);
  const [initAskCheckoutTimeAndMileage, setInitAskCheckoutTimeAndMileage] = useState<boolean>(false);

  const [restrictStationVisibility, setRestrictStationVisibility] = useState<boolean>(false);
  const [initRestrictStationVisibility, setInitRestrictStationVisibility] = useState<boolean>(false);

  const [skillBasedRequirements, setSkillBasedRequirements] = useState<boolean>(false);
  const [initSkillBasedRequirements, setInitSkillBasedRequirements] = useState<boolean>(false);

  const [enforcementLevel, setEnforcementLevel] = useState<DriverRequirementEnforcementLevel>(
    DriverRequirementEnforcementLevel.Warning
  );
  const [initEnforcementLevel, setInitEnforcementLevel] = useState<DriverRequirementEnforcementLevel | null>(null);

  const [createManifestOutSide, setCreateManifestOutSide] = useState<boolean>(false);
  const [initCreateManifestOutSide, setInitCreateManifestOutSide] = useState<boolean>(false);

  const [stopCompletedAtFutureTime, setStopCompletedAtFutureTime] = useState<boolean>(false);
  const [initStopCompletedAtFutureTime, setInitStopCompletedAtFutureTime] = useState<boolean>(false);

  const [closedNeverReassign, setClosedNeverReassign] = useState<boolean>(false);
  const [initClosedNeverReassign, setInitClosedNeverReassign] = useState<boolean>(false);

  // DP-3194: daily suggested assignment cap
  const [suggestedAssignmentsPerDay, setSuggestedAssignmentsPerDay] = useState<number>(
    DEFAULT_SUGGESTED_ASSIGNMENTS_PER_DAY
  );
  const [initSuggestedAssignmentsPerDay, setInitSuggestedAssignmentsPerDay] = useState<number>(
    DEFAULT_SUGGESTED_ASSIGNMENTS_PER_DAY
  );

  const updateTenantPrefs = useCallback(
    (key: string, value: any) => {
      setTenantPref({
        variables: {
          name: key,
          input: {
            value: JSON.stringify({value: value})
          }
        }
      })
        .then(() => {
          if (tenantPrefsQueryRefetch) tenantPrefsQueryRefetch();
        })
        .catch((error: Error) => {
          log.error(error);
        });
    },
    [setTenantPref, tenantPrefsQueryRefetch]
  );

  // OnChange Handlers
  const handleDriverRequiredChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = event.currentTarget.value as DriverRequirementEnforcementLevel;
    setEnforcementLevel(selectedValue);
  }, []);

  const handleSkillBasedRequirementsChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setSkillBasedRequirements(selectedValue);
  }, []);

  const handleOrderEntryTypeChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = event.currentTarget.value as OrderEntryType;
    setOrderEntryType(selectedValue);
  }, []);

  const handleTimezoneOfJobStopChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setTimezoneOfJobStop(selectedValue);
  }, []);

  const handleRequireArrAndDepTimesChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setRequireArrAndDepTimes(selectedValue);
  }, []);

  const handleCanUnassignPartialJobChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setCanUnassignPartialJob(selectedValue);
  }, []);

  const handleAssignmentSubmittedNotificationChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    setAssignmentSubmittedNotification(Boolean(JSON.parse(event.currentTarget.value)));
  }, []);

  const handleDefaultManifestStatusChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = event.currentTarget.value;
    setDefaultManifestStatus(selectedValue as ManifestStatusType);
  }, []);

  const handleIdexxCompleteStopLogicChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setIdexxCompleteStopLogic(selectedValue);
  }, []);

  const handleAskCheckoutTimeAndMilageChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setAskCheckoutTimeAndMileage(selectedValue);
  }, []);

  const handleRestrictStationVisibilityChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setRestrictStationVisibility(selectedValue);
  }, []);

  const handleCreateManifestOutSideChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setCreateManifestOutSide(selectedValue);
  }, []);

  const handleStopCompletedAtFutureTimeChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setStopCompletedAtFutureTime(selectedValue);
  }, []);

  const handleClosedNeverReassignChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setClosedNeverReassign(selectedValue);
  }, []);

  // DP-3194: suggested assignment daily cap
  const handleSuggestedAssignmentsPerDayChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    let selectedValue = parseInt(event.currentTarget.value);
    if (isNaN(selectedValue) || selectedValue < MIN_SUGGESTED_ASSIGNMENTS_PER_DAY) {
      selectedValue = MIN_SUGGESTED_ASSIGNMENTS_PER_DAY;
    }
    setSuggestedAssignmentsPerDay(selectedValue);
  }, []);

  //Dirty Checks
  const isEnforcementLevelDirty = useCallback(() => {
    return initEnforcementLevel !== enforcementLevel;
  }, [enforcementLevel, initEnforcementLevel]);

  const isSkillBasedRequirementsDirty = useCallback(() => {
    return initSkillBasedRequirements !== skillBasedRequirements;
  }, [initSkillBasedRequirements, skillBasedRequirements]);

  const isOrderEntryTypeDirty = useCallback(() => {
    return initOrderEntryType !== orderEntryType;
  }, [initOrderEntryType, orderEntryType]);

  const isTimezoneOfJobStopDirty = useCallback(() => {
    return initTimezoneOfJobStop !== timezoneOfJobStop;
  }, [initTimezoneOfJobStop, timezoneOfJobStop]);

  const isRequireArrAndDepTimesDirty = useCallback(() => {
    return initRequireArrAndDepTimes !== requireArrAndDepTimes;
  }, [initRequireArrAndDepTimes, requireArrAndDepTimes]);

  const isCanUnassignPartialJobDirty = useCallback(() => {
    return initCanUnassignPartialJob !== canUnassignPartialJob;
  }, [canUnassignPartialJob, initCanUnassignPartialJob]);

  const isAssignmentSubmittedNotificationDirty = useCallback(() => {
    return initAssignmentSubmittedNotification !== assignmentSubmittedNotification;
  }, [initAssignmentSubmittedNotification, assignmentSubmittedNotification]);

  const isDefaultManifestStatusDirty = useCallback(() => {
    return initDefaultManifestStatus !== defaultManifestStatus;
  }, [defaultManifestStatus, initDefaultManifestStatus]);

  const isIdexxCompleteStopLogicDirty = useCallback(() => {
    return initIdexxCompleteStopLogic !== idexxCompleteStopLogic;
  }, [idexxCompleteStopLogic, initIdexxCompleteStopLogic]);

  const isAskCheckoutTimeAndMileageDirty = useCallback(() => {
    return initAskCheckoutTimeAndMileage !== askCheckoutTimeAndMileage;
  }, [askCheckoutTimeAndMileage, initAskCheckoutTimeAndMileage]);

  const isRestrictStationVisibilityDirty = useCallback(() => {
    return initRestrictStationVisibility !== restrictStationVisibility;
  }, [initRestrictStationVisibility, restrictStationVisibility]);

  const isCreateManifestOutSideDirty = useCallback(() => {
    return initCreateManifestOutSide !== createManifestOutSide;
  }, [initCreateManifestOutSide, createManifestOutSide]);

  const isStopCompletedAtFutureTimeDirty = useCallback(() => {
    return initStopCompletedAtFutureTime !== stopCompletedAtFutureTime;
  }, [initStopCompletedAtFutureTime, stopCompletedAtFutureTime]);

  const isClosedNeverReassignDirty = useCallback(() => {
    return initClosedNeverReassign !== closedNeverReassign;
  }, [initClosedNeverReassign, closedNeverReassign]);

  const isSuggestedAssignmentsPerDayDirty = useCallback(() => {
    return initSuggestedAssignmentsPerDay !== suggestedAssignmentsPerDay;
  }, [initSuggestedAssignmentsPerDay, suggestedAssignmentsPerDay]);

  const isSaveChange = () => {
    return (
      isEnforcementLevelDirty() ||
      isOrderEntryTypeDirty() ||
      isTimezoneOfJobStopDirty() ||
      isRequireArrAndDepTimesDirty() ||
      isCanUnassignPartialJobDirty() ||
      isAssignmentSubmittedNotificationDirty() ||
      isDefaultManifestStatusDirty() ||
      isIdexxCompleteStopLogicDirty() ||
      isAskCheckoutTimeAndMileageDirty() ||
      isRestrictStationVisibilityDirty() ||
      isCreateManifestOutSideDirty() ||
      isSkillBasedRequirementsDirty() ||
      isStopCompletedAtFutureTimeDirty() ||
      isClosedNeverReassignDirty() ||
      isSuggestedAssignmentsPerDayDirty()
    );
  };

  const handleSaveSetting = useCallback(() => {
    if (isEnforcementLevelDirty()) {
      updateTenantPrefs(TenantPreferences.driverRequirementEnforcement, enforcementLevel);
    }
    if (isOrderEntryTypeDirty()) {
      updateTenantPrefs(TenantPreferences.orderEntryType, orderEntryType);
    }
    if (isTimezoneOfJobStopDirty()) {
      updateTenantPrefs(TenantPreferences.timezoneOfJobStop, timezoneOfJobStop);
    }
    if (isRequireArrAndDepTimesDirty()) {
      updateTenantPrefs(TenantPreferences.requireArrAndDepTimes, requireArrAndDepTimes);
    }
    if (isCanUnassignPartialJobDirty()) {
      updateTenantPrefs(TenantPreferences.unassignPartialJob, canUnassignPartialJob);
    }
    if (isAssignmentSubmittedNotificationDirty()) {
      updateTenantPrefs(TenantPreferences.assignmentSubmittedNotification, assignmentSubmittedNotification);
    }
    if (isDefaultManifestStatusDirty()) {
      updateTenantPrefs(TenantPreferences.defaultManifestStatus, defaultManifestStatus);
    }
    if (isIdexxCompleteStopLogicDirty()) {
      updateTenantPrefs(TenantPreferences.idexxCompleteStopLogic, idexxCompleteStopLogic);
    }
    if (isAskCheckoutTimeAndMileageDirty()) {
      updateTenantPrefs(TenantPreferences.askCheckoutTimeAndMilage, askCheckoutTimeAndMileage);
    }
    if (isRestrictStationVisibilityDirty()) {
      updateTenantPrefs(TenantPreferences.restrictStationVisibility, restrictStationVisibility);
    }
    if (isCreateManifestOutSideDirty()) {
      updateTenantPrefs(TenantPreferences.createManifestOutSide, createManifestOutSide);
    }
    if (isSkillBasedRequirementsDirty()) {
      updateTenantPrefs(TenantPreferences.skillsBasedRequirements, skillBasedRequirements);
    }
    if (isStopCompletedAtFutureTimeDirty()) {
      updateTenantPrefs(TenantPreferences.stopCompletedAtFutureTime, stopCompletedAtFutureTime);
    }
    if (isClosedNeverReassignDirty()) {
      updateTenantPrefs(TenantPreferences.closedNeverReassign, closedNeverReassign);
    }
    if (isSuggestedAssignmentsPerDayDirty()) {
      updateTenantPrefs(TenantPreferences.suggestedAssignmentsPerDay, suggestedAssignmentsPerDay);
    }
  }, [
    isStopCompletedAtFutureTimeDirty,
    isEnforcementLevelDirty,
    isOrderEntryTypeDirty,
    isTimezoneOfJobStopDirty,
    isRequireArrAndDepTimesDirty,
    isCanUnassignPartialJobDirty,
    isAssignmentSubmittedNotificationDirty,
    isDefaultManifestStatusDirty,
    isIdexxCompleteStopLogicDirty,
    isAskCheckoutTimeAndMileageDirty,
    isRestrictStationVisibilityDirty,
    isCreateManifestOutSideDirty,
    isSkillBasedRequirementsDirty,
    isClosedNeverReassignDirty,
    isSuggestedAssignmentsPerDayDirty,
    suggestedAssignmentsPerDay,
    updateTenantPrefs,
    stopCompletedAtFutureTime,
    enforcementLevel,
    orderEntryType,
    timezoneOfJobStop,
    requireArrAndDepTimes,
    canUnassignPartialJob,
    assignmentSubmittedNotification,
    defaultManifestStatus,
    idexxCompleteStopLogic,
    askCheckoutTimeAndMileage,
    restrictStationVisibility,
    createManifestOutSide,
    skillBasedRequirements,
    closedNeverReassign
  ]);

  const handleCancelSetting = useCallback(() => {
    setEnforcementLevel(initEnforcementLevel as DriverRequirementEnforcementLevel);
    setOrderEntryType(initOrderEntryType as OrderEntryType);
    setTimezoneOfJobStop(initTimezoneOfJobStop);
    setRequireArrAndDepTimes(initRequireArrAndDepTimes);
    setCanUnassignPartialJob(initCanUnassignPartialJob);
    setAssignmentSubmittedNotification(initAssignmentSubmittedNotification);
    setDefaultManifestStatus(initDefaultManifestStatus);
    setAskCheckoutTimeAndMileage(initAskCheckoutTimeAndMileage);
    setRestrictStationVisibility(initRestrictStationVisibility);
    setCreateManifestOutSide(initCreateManifestOutSide);
    setSkillBasedRequirements(initSkillBasedRequirements);
    setStopCompletedAtFutureTime(initStopCompletedAtFutureTime);
    setClosedNeverReassign(initClosedNeverReassign);
    setSuggestedAssignmentsPerDay(initSuggestedAssignmentsPerDay);
  }, [
    initEnforcementLevel,
    initOrderEntryType,
    initTimezoneOfJobStop,
    initRequireArrAndDepTimes,
    initCanUnassignPartialJob,
    initAssignmentSubmittedNotification,
    initDefaultManifestStatus,
    initAskCheckoutTimeAndMileage,
    initRestrictStationVisibility,
    initCreateManifestOutSide,
    initSkillBasedRequirements,
    initStopCompletedAtFutureTime,
    initClosedNeverReassign,
    initSuggestedAssignmentsPerDay
  ]);

  const renderGeneralSettings = useMemo(() => {
    const retVal: ContentProps[] = [];

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Order Entry Version",
      contentOptions: [
        {
          label: "Current",
          value: OrderEntryType.Current
        },
        {
          label: "Legacy",
          value: OrderEntryType.Legacy
        }
      ],
      contentInformation:
        "Sets which version of order entry to use.  The current CSHTML version, or the legacy version.",
      selectedOption: orderEntryType,
      handleChangeOption: handleOrderEntryTypeChange,
      renderPropComponents: renderRadioGroupProps
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Time zone of job/stop",
      contentOptions: [
        {
          group: "timezone",
          label: "Yes",
          value: "true"
        },
        {
          group: "timezone",
          label: "No",
          value: "false"
        }
      ],
      contentInformation:
        "Allow show the times of stops and jobs based on the it's timezone. Default: No = Browser's timezone.",
      selectedOption: _.toString(timezoneOfJobStop),
      handleChangeOption: handleTimezoneOfJobStopChange,
      renderPropComponents: renderRadioGroupProps
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Require arrival & departure times",
      contentOptions: [
        {
          group: "ArrivalAndDepartureTimes",
          label: "Yes",
          value: "true"
        },
        {
          group: "ArrivalAndDepartureTimes",
          label: "No",
          value: "false"
        }
      ],
      contentInformation: "The arrival/departure time would be required or not when mark stop as completed.",
      selectedOption: _.toString(requireArrAndDepTimes),
      handleChangeOption: handleRequireArrAndDepTimesChange,
      renderPropComponents: renderRadioGroupProps
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Allow Unassign Partially Complete Job",
      contentInformation: "Allow dispatchers to unassign partially complete jobs.",
      contentOptions: [
        {
          group: "unassignPartialJob",
          label: "Yes",
          value: "true"
        },
        {
          group: "unassignPartialJob",
          label: "No",
          value: "false"
        }
      ],
      handleChangeOption: handleCanUnassignPartialJobChange,
      renderPropComponents: renderRadioGroupProps,
      selectedOption: _.toString(canUnassignPartialJob)
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Assignment Submitted Notification",
      contentInformation: "Show the 'Assignment Submitted' pop-up after jobs are assigned to a driver.",
      contentOptions: [
        {
          group: "assignment-submitted-notification",
          label: "Yes",
          value: "true"
        },
        {
          group: "assignment-submitted-notification",
          label: "No",
          value: "false"
        }
      ],
      handleChangeOption: handleAssignmentSubmittedNotificationChange,
      renderPropComponents: renderRadioGroupProps,
      selectedOption: _.toString(assignmentSubmittedNotification)
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Default New Manifest Status",
      contentInformation: "The default status when creating new manifest.",
      contentOptions: [
        {
          group: "defaultManifestStatus",
          label: "Active",
          value: ManifestStatusType.Active
        },
        {
          group: "defaultManifestStatus",
          label: "Future",
          value: ManifestStatusType.Future
        },
        {
          group: "defaultManifestStatus",
          label: "Inactive",
          value: ManifestStatusType.Inactive
        }
      ],
      handleChangeOption: handleDefaultManifestStatusChange,
      renderPropComponents: renderRadioGroupProps,
      selectedOption: _.toString(defaultManifestStatus)
    });

    if (idexxFeatureEnabled) {
      retVal.push({
        ...ContentOptionProps,
        contentTitle: "Enable IDEXX Complete Stop Logic",
        contentInformation: "Enable IDEXX Complete Stop Logic.", // TODO: add more information
        contentOptions: [
          {
            group: "idexxCompleteStopLogic",
            label: "Yes",
            value: "true"
          },
          {
            group: "idexxCompleteStopLogic",
            label: "No",
            value: "false"
          }
        ],
        handleChangeOption: handleIdexxCompleteStopLogicChange,
        renderPropComponents: renderRadioGroupProps,
        selectedOption: _.toString(idexxCompleteStopLogic)
      });
    }
    if (askManifestCheckoutInfoEnabled) {
      retVal.push({
        ...ContentOptionProps,
        contentTitle: "Ask for start/end time & mileage when checking out driver",
        contentInformation: "Ask for start/end time & mileage when checking out driver.",
        contentOptions: [
          {
            group: "askCheckoutTimeAndMileage",
            label: "Yes",
            value: "true"
          },
          {
            group: "askCheckoutTimeAndMileage",
            label: "No",
            value: "false"
          }
        ],
        handleChangeOption: handleAskCheckoutTimeAndMilageChange,
        renderPropComponents: renderRadioGroupProps,
        selectedOption: _.toString(askCheckoutTimeAndMileage)
      });
    }

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Restrict Station Visibility",
      contentInformation: "Restrict Station Visibility.",
      contentOptions: [
        {
          group: "restrictStationVisibility",
          label: "Yes",
          value: "true"
        },
        {
          group: "restrictStationVisibility",
          label: "No",
          value: "false"
        }
      ],
      handleChangeOption: handleRestrictStationVisibilityChange,
      renderPropComponents: renderRadioGroupProps,
      selectedOption: _.toString(restrictStationVisibility)
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Create Manifests Outside Selected Driver Group(s)",
      contentInformation: "Allow create manifests outside selected driver Group(s).",
      contentOptions: [
        {
          group: "createManifestOutSide",
          label: "Yes",
          value: "true"
        },
        {
          group: "createManifestOutSide",
          label: "No",
          value: "false"
        }
      ],
      handleChangeOption: handleCreateManifestOutSideChange,
      renderPropComponents: renderRadioGroupProps,
      selectedOption: _.toString(createManifestOutSide)
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Allow Marking Stop Complete at Future Time",
      contentOptions: [
        {
          group: "stopCompletedAtFutureTime",
          label: "Yes",
          value: "true"
        },
        {
          group: "stopCompletedAtFutureTime",
          label: "No",
          value: "false"
        }
      ],
      contentInformation: "Allow marking stop complete at future time.",
      selectedOption: _.toString(stopCompletedAtFutureTime),
      handleChangeOption: handleStopCompletedAtFutureTimeChange,
      renderPropComponents: renderRadioGroupProps
    });

    if (closedNeverReassignEnabled) {
      retVal.push({
        ...ContentOptionProps,
        contentTitle: "Prevent jobs that were ever complete from being reassigned or unassigned",
        contentInformation: "Prevent jobs that were ever complete from being reassigned or unassigned.",
        contentOptions: [
          {
            group: "closedNeverReassign",
            label: "Yes",
            value: "true"
          },
          {
            group: "closedNeverReassign",
            label: "No",
            value: "false"
          }
        ],
        handleChangeOption: handleClosedNeverReassignChange,
        renderPropComponents: renderRadioGroupProps,
        selectedOption: _.toString(closedNeverReassign)
      });
    }

    // DP-3194 / DP-3233: suggested assignment daily cap — internal e-Courier admins only.
    if (suggestedAssignmentEnabled && isSuperUser) {
      retVal.push({
        ...ContentNumericProp,
        contentTitle: "Number of Suggested Assignments per Day",
        contentInformation: (
          <div>
            Maximum number of suggested assignments this company can run each day.
            <br />
            Set to 0 to block all suggested assignments.
            <br />
            Resets daily at 12:01 AM Eastern.
          </div>
        ),
        contentUnit: "suggestions",
        selectedOption: _.toString(suggestedAssignmentsPerDay),
        handleChangeOption: handleSuggestedAssignmentsPerDayChange,
        renderPropComponents: renderNumericTextBox
      });
    }

    return retVal;
  }, [
    orderEntryType,
    handleOrderEntryTypeChange,
    timezoneOfJobStop,
    handleTimezoneOfJobStopChange,
    requireArrAndDepTimes,
    handleRequireArrAndDepTimesChange,
    handleCanUnassignPartialJobChange,
    canUnassignPartialJob,
    handleAssignmentSubmittedNotificationChange,
    assignmentSubmittedNotification,
    handleDefaultManifestStatusChange,
    defaultManifestStatus,
    idexxFeatureEnabled,
    askManifestCheckoutInfoEnabled,
    handleCreateManifestOutSideChange,
    createManifestOutSide,
    handleIdexxCompleteStopLogicChange,
    idexxCompleteStopLogic,
    handleAskCheckoutTimeAndMilageChange,
    askCheckoutTimeAndMileage,
    handleRestrictStationVisibilityChange,
    restrictStationVisibility,
    stopCompletedAtFutureTime,
    handleStopCompletedAtFutureTimeChange,
    closedNeverReassignEnabled,
    closedNeverReassign,
    handleClosedNeverReassignChange,
    isSuperUser,
    suggestedAssignmentEnabled,
    suggestedAssignmentsPerDay,
    handleSuggestedAssignmentsPerDayChange
  ]);

  const renderDriverRequirementSettings = useMemo(() => {
    const driverRequirementsSettings: ContentProps[] = [];
    if (skillsEnabled) {
      driverRequirementsSettings.push({
        ...ContentOptionProps,
        contentTitle: "Use Skill Based Requirements",
        contentInformation: "Use Skill Based Requirements.",
        contentOptions: [
          {
            group: "skillBasedRequirements",
            label: "Yes",
            value: "true"
          },
          {
            group: "skillBasedRequirements",
            label: "No",
            value: "false"
          }
        ],
        selectedOption: _.toString(skillBasedRequirements),
        handleChangeOption: handleSkillBasedRequirementsChange,
        renderPropComponents: renderRadioGroupProps
      });
    }
    driverRequirementsSettings.push({
      ...ContentOptionProps,
      contentTitle: "Assignment to non-qualified drivers",
      contentOptions: [
        {
          label: "Always allow",
          value: DriverRequirementEnforcementLevel.Allow
        },
        {
          label: "Allow with confirmation",
          value: DriverRequirementEnforcementLevel.Warning
        },
        {
          label: "Never allow",
          value: DriverRequirementEnforcementLevel.Prevent
        }
      ],
      contentInformation: "Assignment of the jobs with driver requirements to the drivers with missing requirements.",
      selectedOption: enforcementLevel,
      handleChangeOption: handleDriverRequiredChange
    });
    return driverRequirementsSettings;
  }, [
    enforcementLevel,
    handleDriverRequiredChange,
    skillsEnabled,
    skillBasedRequirements,
    handleSkillBasedRequirementsChange
  ]);

  const handleEnforcementLevel = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<DriverRequirementEnforcementLevel> = extractSimplePref(
      prefs,
      TenantPreferences.driverRequirementEnforcement,
      DriverRequirementEnforcementLevel.Warning
    );
    setEnforcementLevel(prefVal.value as DriverRequirementEnforcementLevel);
    setInitEnforcementLevel(prefVal.value as DriverRequirementEnforcementLevel);
  }, []);

  const handleSkillBasedRequirements = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.skillsBasedRequirements, false);
    setSkillBasedRequirements(prefVal.value as boolean);
    setInitSkillBasedRequirements(prefVal.value as boolean);
  }, []);

  const handleOrderEntryType = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<OrderEntryType> = extractSimplePref(
      prefs,
      TenantPreferences.orderEntryType,
      OrderEntryType.Current
    );
    setOrderEntryType(prefVal.value as OrderEntryType);
    setInitOrderEntryType(prefVal.value as OrderEntryType);
  }, []);

  const handleTimezoneOfJobStop = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.timezoneOfJobStop, false);
    setTimezoneOfJobStop(prefVal.value as boolean);
    setInitTimezoneOfJobStop(prefVal.value as boolean);
  }, []);

  const handleRequireArrAndDepTimes = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.requireArrAndDepTimes, false);
    setRequireArrAndDepTimes(prefVal.value as boolean);
    setInitRequireArrAndDepTimes(prefVal.value as boolean);
  }, []);

  const handleCanUnassignPartialJob = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.unassignPartialJob, false);
    setCanUnassignPartialJob(prefVal.value as boolean);
    setInitCanUnassignPartialJob(prefVal.value as boolean);
  }, []);

  const handleAssignmentSubmittedNotification = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(
      prefs,
      TenantPreferences.assignmentSubmittedNotification,
      ASSIGNMENT_SUBMITTED_NOTIFICATION_DEFAULT
    );
    setAssignmentSubmittedNotification(prefVal.value as boolean);
    setInitAssignmentSubmittedNotification(prefVal.value as boolean);
  }, []);

  const handleDefaultManifestStatus = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<ManifestStatusType> = extractSimplePref(
      prefs,
      TenantPreferences.defaultManifestStatus,
      ManifestStatusType.Active
    );
    setDefaultManifestStatus(prefVal.value as ManifestStatusType);
    setInitDefaultManifestStatus(prefVal.value as ManifestStatusType);
  }, []);

  const handleIdexxCompleteStopLogic = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.idexxCompleteStopLogic, false);
    setIdexxCompleteStopLogic(prefVal.value as boolean);
    setInitIdexxCompleteStopLogic(prefVal.value as boolean);
  }, []);

  const handleAskCheckoutTimeAndMilage = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.askCheckoutTimeAndMilage, false);
    setAskCheckoutTimeAndMileage(prefVal.value as boolean);
    setInitAskCheckoutTimeAndMileage(prefVal.value as boolean);
  }, []);

  const handleRestrictStationVisibility = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.restrictStationVisibility, false);
    setRestrictStationVisibility(prefVal.value as boolean);
    setInitRestrictStationVisibility(prefVal.value as boolean);
  }, []);

  const handleCreateManifestOutSide = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.createManifestOutSide, false);
    setCreateManifestOutSide(prefVal.value as boolean);
    setInitCreateManifestOutSide(prefVal.value as boolean);
  }, []);

  const handleStopCompletedAtFutureTime = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.stopCompletedAtFutureTime, false);
    setStopCompletedAtFutureTime(prefVal.value as boolean);
    setInitStopCompletedAtFutureTime(prefVal.value as boolean);
  }, []);

  const handleClosedNeverReassign = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.closedNeverReassign, false);
    setClosedNeverReassign(prefVal.value as boolean);
    setInitClosedNeverReassign(prefVal.value as boolean);
  }, []);

  // DP-3194: load suggested assignment daily cap
  const handleSuggestedAssignmentsPerDay = useCallback((prefs: Pref[]) => {
    const prefVal = extractSimplePref<number>(
      prefs,
      TenantPreferences.suggestedAssignmentsPerDay,
      DEFAULT_SUGGESTED_ASSIGNMENTS_PER_DAY
    );
    const val = Number(prefVal.value);
    const capped =
      Number.isFinite(val) && val >= MIN_SUGGESTED_ASSIGNMENTS_PER_DAY ? val : DEFAULT_SUGGESTED_ASSIGNMENTS_PER_DAY;
    setSuggestedAssignmentsPerDay(capped);
    setInitSuggestedAssignmentsPerDay(capped);
  }, []);

  useEffect(() => {
    log.debug("Tenant preferences loaded: ", tenantPreferences);
    handleEnforcementLevel(tenantPreferences);
    handleOrderEntryType(tenantPreferences);
    handleTimezoneOfJobStop(tenantPreferences);
    handleRequireArrAndDepTimes(tenantPreferences);
    handleCanUnassignPartialJob(tenantPreferences);
    handleAssignmentSubmittedNotification(tenantPreferences);
    handleDefaultManifestStatus(tenantPreferences);
    handleIdexxCompleteStopLogic(tenantPreferences);
    handleAskCheckoutTimeAndMilage(tenantPreferences);
    handleRestrictStationVisibility(tenantPreferences);
    handleCreateManifestOutSide(tenantPreferences);
    handleSkillBasedRequirements(tenantPreferences);
    handleStopCompletedAtFutureTime(tenantPreferences);
    handleClosedNeverReassign(tenantPreferences);
    handleSuggestedAssignmentsPerDay(tenantPreferences);
  }, [
    handleCanUnassignPartialJob,
    handleAssignmentSubmittedNotification,
    handleEnforcementLevel,
    handleOrderEntryType,
    handleRequireArrAndDepTimes,
    handleTimezoneOfJobStop,
    handleDefaultManifestStatus,
    handleIdexxCompleteStopLogic,
    tenantPreferences,
    handleAskCheckoutTimeAndMilage,
    handleRestrictStationVisibility,
    handleCreateManifestOutSide,
    handleSkillBasedRequirements,
    handleStopCompletedAtFutureTime,
    handleClosedNeverReassign,
    handleSuggestedAssignmentsPerDay
  ]);

  useEffect(() => {
    const data = setTenantPrefData?.setTenantPref as UserPref;
    log.debug("Pref saved: ", data);
    if (data && data.name === TenantPreferences.orderEntryType) {
      handleOrderEntryType([data]);
    }
    if (data && data.name === TenantPreferences.driverRequirementEnforcement) {
      handleEnforcementLevel([data]);
    }
    if (data && data.name === TenantPreferences.timezoneOfJobStop) {
      handleTimezoneOfJobStop([data]);
    }
    if (data && data.name === TenantPreferences.requireArrAndDepTimes) {
      handleRequireArrAndDepTimes([data]);
    }
    if (data && data.name === TenantPreferences.unassignPartialJob) {
      handleCanUnassignPartialJob([data]);
    }
    if (data && data.name === TenantPreferences.assignmentSubmittedNotification) {
      handleAssignmentSubmittedNotification([data]);
    }
    if (data && data.name === TenantPreferences.defaultManifestStatus) {
      handleDefaultManifestStatus([data]);
    }
    if (data && data.name === TenantPreferences.askCheckoutTimeAndMilage) {
      handleAskCheckoutTimeAndMilage([data]);
    }
    if (data && data.name === TenantPreferences.restrictStationVisibility) {
      handleRestrictStationVisibility([data]);
    }
    if (data && data.name === TenantPreferences.createManifestOutSide) {
      handleCreateManifestOutSide([data]);
    }
    if (data && data.name === TenantPreferences.skillsBasedRequirements) {
      handleSkillBasedRequirements([data]);
    }
    if (data && data.name === TenantPreferences.stopCompletedAtFutureTime) {
      handleStopCompletedAtFutureTime([data]);
    }
    if (data && data.name === TenantPreferences.closedNeverReassign) {
      handleClosedNeverReassign([data]);
    }
    if (data && data.name === TenantPreferences.suggestedAssignmentsPerDay) {
      handleSuggestedAssignmentsPerDay([data]);
    }
  }, [
    handleEnforcementLevel,
    handleOrderEntryType,
    setTenantPrefData?.setTenantPref,
    handleTimezoneOfJobStop,
    handleRequireArrAndDepTimes,
    handleCanUnassignPartialJob,
    handleAssignmentSubmittedNotification,
    handleDefaultManifestStatus,
    handleAskCheckoutTimeAndMilage,
    handleRestrictStationVisibility,
    handleCreateManifestOutSide,
    handleSkillBasedRequirements,
    handleStopCompletedAtFutureTime,
    handleClosedNeverReassign,
    handleSuggestedAssignmentsPerDay
  ]);

  return (
    <Container>
      <Header>
        <HeaderText>Company Settings</HeaderText>
      </Header>
      <Body>
        <Section
          sectionName="general-settings-section"
          sectionTitle="General Settings"
          render={renderGeneralSettings}
        />
        {driverRequirementsEnabled && (
          <Section
            sectionName="driver-requirements-section"
            sectionTitle="Driver Requirement Settings"
            render={renderDriverRequirementSettings}
          />
        )}
      </Body>
      <Footer>
        <CancelButton
          data-testid="cancel-button"
          disabled={!isSaveChange() || isSetTenantPrefLoading}
          onClick={handleCancelSetting}
          intent="none"
          outlined
        >
          Cancel
        </CancelButton>
        <SaveButton
          data-testid="save-button"
          disabled={!isSaveChange() || isSetTenantPrefLoading}
          onClick={handleSaveSetting}
          intent="primary"
        >
          Save
        </SaveButton>
      </Footer>
    </Container>
  );
};

export default General;
